<template>
  <Field :name="name || ''" :rules="rules" :model-value="currentValue" v-slot="{ field, errorMessage }">
    <div class="relative w-full">
      <InputLabel
        :for="id"
        :placeholder="placeholder"
        :value="currentValue"
        :class="errorMessage ? 'text-error' : 'text-text'"
      />
      <input
        v-bind="field"
        :name="name"
        :id="id"
        :value="currentValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :class="[
          'w-full tabular-nums placeholder-transparent border rounded-sm bg-surface-light h-14 text-headings focus:outline-none focus:ring-1 focus:ring-input-border transition-colors px-4',
          currentValue && !props.hideTitle ? 'pt-6 pb-2' : 'py-4',
          errorMessage ? 'border-error text-error' : 'border-input-border',
        ]"
        @input="onInput"
        @keydown="onKeydown"
        maxlength="11"
        inputmode="numeric"
        pattern="[0-9]*"
        ref="inputRef"
      />
      <span
        class="absolute tabular-nums left-4 pt-4 top-1/2 -translate-y-1/2 pointer-events-none select-none text-headings"
        :style="{ zIndex: 1 }"
        v-if="maskDisplay.length > 0"
      >
        <span style="opacity: 0">{{ currentValue }}</span
        >{{ maskDisplay }}
      </span>
    </div>
    <div v-if="errorMessage" class="text-sm block pt-2 text-error">
      {{ errorMessage }}
    </div>
  </Field>
</template>

<script setup lang="ts">
  import { Field } from 'vee-validate';
  import { computed,ref } from 'vue';

  import InputLabel from './InputLabel.vue';
  import { type InputProps,useInput } from './InputMixin';

  interface Props extends InputProps<string> {
    placeholder?: string;
    hideTitle?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {});

  const emit = defineEmits<{ 'update:modelValue': [value: string] }>();
  const { currentValue } = useInput(props, (event: string, value: string) =>
    emit('update:modelValue', value)
  );

  const inputRef = ref<HTMLInputElement | null>(null);

  const mask = '______-____';
  const maskDisplay = computed(() => {
    if (!currentValue.value) return '';

    let value = currentValue.value.replace(/[^0-9]/g, '');
    if (value.length > 6) {
      value = value.slice(0, 6) + '-' + value.slice(6, 10);
    } else {
      value = value.slice(0, 6);
    }

    let maskedValue = '';
    for (let i = 0; i < mask.length; i++) {
      if (value[i]) {
        maskedValue += ' ';
      } else {
        maskedValue += mask[i];
      }
    }
    return maskedValue;
  });

  function onInput(event: Event) {
    let value = (event.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
    if (value.length > 6) value = value.slice(0, 6) + '-' + value.slice(6, 10);
    else value = value.slice(0, 6);
    currentValue.value = value;
    console.log('Input value:', value);
    console.log('CurrentValue:', currentValue.value);
    console.log('V-Model is:', props.modelValue);
  }

  function onKeydown(event: KeyboardEvent) {
    if (
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      [
        'Backspace',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Delete',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      return;
    }
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }
</script>
